import { captureException } from '@sentry/sveltekit';

/**
 * @typedef {{
 *   code?: string | null;
 *   details?: string | null;
 *   hint?: string | null;
 *   message?: string | null;
 *   name?: string | null;
 *   status?: number | string | null;
 * }} SupabaseErrorLike
 */

/**
 * @typedef {{
 *   message?: string;
 *   operation?: string;
 *   table?: string;
 *   rpc?: string;
 *   extra?: Record<string, unknown>;
 *   level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
 * }} CaptureSupabaseErrorOptions
 */

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function is_object(value) {
    return Boolean(value) && typeof value === 'object';
}

/**
 * @param {unknown} value
 * @returns {string | null | undefined}
 */
function to_optional_string(value) {
    if (value == null) return /** @type {null | undefined} */ (value);
    return typeof value === 'string' ? value : String(value);
}

/**
 * @param {unknown} value
 * @returns {number | string | null | undefined}
 */
function to_optional_status(value) {
    if (value == null) return /** @type {null | undefined} */ (value);
    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }
    return String(value);
}

/**
 * @param {unknown} error
 * @returns {SupabaseErrorLike}
 */
function normalize_supabase_error(error) {
    if (!is_object(error)) {
        return { message: String(error || '') };
    }

    const obj = /** @type {Record<string, unknown>} */ (error);

    return {
        code: to_optional_string(obj.code),
        details: to_optional_string(obj.details),
        hint: to_optional_string(obj.hint),
        message: to_optional_string(obj.message),
        name: to_optional_string(obj.name),
        status: to_optional_status(obj.status)
    };
}

/**
 * @param {unknown} error
 * @param {string} fallback_message
 * @returns {Error}
 */
function to_error(error, fallback_message) {
    if (error instanceof Error) {
        return error;
    }

    const normalized = normalize_supabase_error(error);
    const message = normalized.message || fallback_message;
    const wrapped = new Error(message);
    wrapped.name = normalized.name || 'SupabaseClientError';

    /** @type {Error & { cause?: unknown }} */ (wrapped).cause = error;
    return wrapped;
}

/**
 * @param {SupabaseErrorLike} normalized
 * @returns {boolean}
 */
function is_failed_fetch(normalized) {
    const source = `${normalized.message || ''} ${normalized.details || ''}`;
    return /failed to fetch/i.test(source);
}

/**
 * Single client-side path for Supabase error capture in Sentry.
 * Ensures object-shaped Supabase errors become real Error events.
 *
 * @param {unknown} error
 * @param {CaptureSupabaseErrorOptions} [options]
 */
export function capture_supabase_error(error, options = {}) {
    if (!error) return;

    const normalized = normalize_supabase_error(error);
    const fallback_message = options.message || 'Supabase client error';
    const wrapped_error = to_error(error, fallback_message);
    const level =
        options.level || (is_failed_fetch(normalized) ? 'warning' : 'error');

    /** @type {Record<string, string>} */
    const tags = {
        source: 'supabase-client'
    };

    if (options.operation) tags.operation = options.operation;
    if (options.table) tags.supabase_table = options.table;
    if (options.rpc) tags.supabase_rpc = options.rpc;
    if (normalized.code) tags.supabase_code = normalized.code;

    captureException(wrapped_error, {
        level,
        tags,
        extra: {
            message: fallback_message,
            operation: options.operation || '',
            table: options.table || '',
            rpc: options.rpc || '',
            supabase_error: normalized,
            ...(options.extra || {})
        }
    });
}
